import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Heading,
  Tag,
  Image,
  ImageProps,
  FlexProps,
  Text,
  TagProps,
} from '@everlywell/ui-kit';
import { createSharedButtonProps } from 'components/PageSections/utils';
import RichTextWrapper from 'components/RichTextWrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import { ImageType } from 'utils/types';
import { CallToAction, ContentfulRichText } from 'utils/types/contentfulTypes';

export type ShapeCardProps = FlexProps & {
  tagText?: string | null;
  tagVariant?: TagProps['variant'];
  headingText?: string | null;
  bottomText?: ContentfulRichText | null;
  bottomTextAuthor?: string | null;
  mobileImage?: ImageType | null;
  mobileImageProps?: ImageProps;
  desktopImage?: ImageType | null;
  cta?:
    | (CallToAction & {
        size?: ButtonProps['size'];
        colorScheme?: ButtonProps['colorScheme'];
        variant?: ButtonProps['variant'];
      })
    | null;
  backgroundColor?: string | null;
  textColor?: string | null;
  cardVariant?: 'vertical' | 'horizontal';
  cardTheme?: 'light' | 'dark';
  addImageShape?: boolean;
  onCtaClick?: (label?: string) => void;
};

const ShapeCard = React.forwardRef<HTMLDivElement, ShapeCardProps>(
  (
    {
      tagText,
      tagVariant = 'callout',
      headingText,
      bottomText,
      bottomTextAuthor,
      mobileImage,
      desktopImage,
      cta,
      backgroundColor,
      textColor = 'tints.black',
      cardVariant = 'horizontal',
      cardTheme = 'dark',
      addImageShape = true,
      mobileImageProps,
      onCtaClick,
      ...rest
    },
    ref,
  ) => {
    const sharedButtonProps: ButtonProps = createSharedButtonProps(cta?.url);

    const isVertical = cardVariant === 'vertical';
    const isLightTheme = cardTheme === 'light';

    return (
      <Flex
        ref={ref}
        display="flex"
        flex="1"
        justifyContent="space-between"
        flexDirection={['column', null, isVertical ? null : 'row']}
        position="relative"
        backgroundColor={backgroundColor}
        borderRadius="lg"
        overflow="hidden"
        {...rest}
      >
        {tagText ? (
          <Tag
            position="absolute"
            zIndex="1"
            top="8"
            left="8"
            variant={tagVariant}
          >
            {tagText}
          </Tag>
        ) : null}
        {/* Mobile image*/}
        {mobileImage ? (
          <Box
            display={['flex', null, isVertical ? null : 'none']}
            clipPath={
              addImageShape
                ? 'polygon(100% 0, 100% 100%, 78% 84%, 0% 100%, 0 44%, 0% 0%)'
                : undefined
            }
            flex="1"
            height="260px"
          >
            <Image
              as={GatsbyImage}
              image={mobileImage?.gatsbyImageData}
              margin="0 auto"
              alt={mobileImage?.description || ''}
              maxHeight="inherit"
              {...mobileImageProps}
            />
          </Box>
        ) : null}

        {!isVertical ? (
          <Flex
            flexDirection="column"
            flex={isVertical ? '1' : undefined}
            width={isVertical ? [] : ['auto', null, '47%']}
            alignSelf={[null, null, isVertical ? null : 'flex-end']}
            justifyContent="flex-end"
            minHeight={['115px', '145px']}
            paddingTop={[null, null, 20]}
            paddingLeft="8"
            paddingRight={[8, null, isVertical ? 8 : 0]}
            paddingBottom="8"
          >
            {headingText ? (
              <Heading
                size="md"
                color={isLightTheme ? 'tints.black' : 'tints.white'}
                marginBottom="5"
                textAlign={[isVertical ? 'left' : 'center', null, 'left']}
              >
                {headingText}
              </Heading>
            ) : null}

            {cta ? (
              <Button
                variant={cta?.variant ?? 'primary'}
                colorScheme={cta?.colorScheme ?? 'whiteCS'}
                id={`${cta.label}-promo`}
                width={['100%', null, 'fit-content']}
                size={cta?.size ?? 'sm'}
                onClick={() => onCtaClick?.(cta?.label)}
                {...sharedButtonProps}
              >
                {cta.label}
              </Button>
            ) : null}
          </Flex>
        ) : null}

        {desktopImage && !isVertical ? (
          <Box
            display={['none', null, 'flex']}
            width={isVertical ? [] : ['auto', null, '53%']}
            clipPath={
              addImageShape
                ? 'polygon(0 0, 100% 0%, 100% 100%, 0 100%, 10% 18%)'
                : undefined
            }
          >
            <Image
              as={GatsbyImage}
              image={desktopImage?.gatsbyImageData}
              alt={mobileImage?.description || ''}
            />
          </Box>
        ) : null}

        {desktopImage && isVertical ? (
          <Box
            display={['flex', null, isVertical ? null : 'none']}
            clipPath={
              addImageShape
                ? 'polygon(100% 0, 100% 100%, 78% 84%, 0% 100%, 0 44%, 0% 0%)'
                : undefined
            }
            height="350px"
          >
            <Image
              as={GatsbyImage}
              image={desktopImage?.gatsbyImageData}
              margin="0 auto"
              alt={desktopImage?.description || ''}
              maxHeight="inherit"
            />
          </Box>
        ) : null}

        {isVertical ? (
          <Box
            padding={6}
            minHeight="255px"
            display="flex"
            justifyContent="flex-end"
            flexDirection="column"
          >
            {bottomText ? (
              <Box
                sx={{
                  p: {
                    color: textColor,
                    mb: [6],
                    fontSize: ['xl', '3xl'],
                  },
                  'p:not(:last-child)': {
                    marginBottom: '0',
                  },
                }}
              >
                <RichTextWrapper contentfulRichText={bottomText} />
              </Box>
            ) : null}
            {bottomTextAuthor ? (
              <Text size="md" color={textColor}>
                {bottomTextAuthor}
              </Text>
            ) : null}
          </Box>
        ) : null}
      </Flex>
    );
  },
);

export default ShapeCard;
